import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SearchIcon from '@mui/icons-material/Search';

const FILTER_OPTIONS = [
  'Status',
  'Date',
  'Agent',
  'Label',
  'Rating',
  'Website',
];

/**
 * Left column — chat queue with search, filter placeholders, and session list.
 */
export default function ChatQueueColumn({
  viewMode = 'active',
  sessions = [],
  loading = false,
  selectedSessionId = '',
  onSelectSession,
  formatSessionTime,
  sessionLabel,
}) {
  const [search, setSearch] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);

  const filtered = sessions.filter((session) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const label = sessionLabel(session).toLowerCase();
    return label.includes(q) || session.id.toLowerCase().includes(q);
  });

  return (
    <Paper
      variant="outlined"
      square
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        borderRadius: 0,
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {viewMode === 'history' ? 'Archived' : 'Queue'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filtered.length} {viewMode === 'history' ? 'archived' : 'active'}
          </Typography>
        </Box>
        <Tooltip title="Export sessions (coming soon)">
          <span>
            <IconButton size="small" aria-label="Export sessions" disabled>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListOutlinedIcon fontSize="small" />}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            sx={{ textTransform: 'none' }}
          >
            Add filter
          </Button>
          <Menu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={() => setFilterAnchor(null)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt} disabled onClick={() => setFilterAnchor(null)}>
                {opt}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {search.trim()
              ? 'No sessions match your search.'
              : viewMode === 'history'
                ? 'No archived chat sessions yet.'
                : 'No open chat sessions right now.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((session) => (
              <ListItemButton
                key={session.id}
                selected={session.id === selectedSessionId}
                onClick={() => onSelectSession?.(session.id)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemText
                  primary={sessionLabel(session)}
                  secondary={formatSessionTime(session.updatedAt || session.createdAt)}
                  primaryTypographyProps={{
                    fontWeight: session.id === selectedSessionId ? 700 : 500,
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
}
